import axios from "axios";
import { logger } from "@/utils/logger";
import type { QueryClient } from "@tanstack/react-query";
import { getApiBaseUrl } from "../config/api";
import { getCognitoRedirectUri } from "../config/cognito";
import { decodeToken, isTokenExpired } from "../utils/tokenUtils";
import type { CaseEventIn, RecordCaseEventsResult } from "../utils/caseEventQueue";
import Cookies from "js-cookie";

// TanStack Query cache is keyed per-origin, not per-user — endpoints like
// /api/cohorts and /api/saved_cases share a global key (see useStudyQueries).
// Logout must wipe the cache or the next login on the same origin reads the
// prior user's rows until staleTime expires (issue #376).
let registeredQueryClient: QueryClient | null = null;

/** Pass null to unregister the client during teardown or test cleanup. */
export function registerAuthQueryClient(client: QueryClient | null): void {
  registeredQueryClient = client;
}

const TOKEN_COOKIE_NAME = "a2z_auth_token";
const REFRESH_TOKEN_COOKIE_NAME = "a2z_refresh_token";
const USER_ID_COOKIE_NAME = "a2z_user_id";
const IS_ADMIN_COOKIE_NAME = "a2z_is_admin";
const ADMIN_USER_ID_COOKIE_NAME = "a2z_admin_user_id";
const USER_TYPE_COOKIE_NAME = "a2z_user_type";
// Tri-state agreement cookie: "true" / "false" / absent (role has none).
// PrivateRoute bounces to /agreement only on the literal "false".
const AGREEMENT_ACCEPTED_COOKIE_NAME = "a2z_agreement_accepted";
// The agreement the backend says this user must accept; absent when none.
const AGREEMENT_TYPE_COOKIE_NAME = "a2z_agreement_type";
const COOKIE_EXPIRY_DAYS = 7;
const ACCESS_TOKEN_EXPIRY_DAYS = 1;

// sessionStorage keys used to stash the admin's tokens while impersonating
// another user. Cleared on stopImpersonating(). sessionStorage (not cookies)
// so the restore stash is bounded to the tab that started impersonation;
// every other tab on the same origin reads the impersonation state off the
// cookie JWT's `impersonated_by` claim instead.
const IMP_STASH_PREFIX = "a2z_imp_stash_";
const IMP_KEYS = {
  token: `${IMP_STASH_PREFIX}token`,
  refreshToken: `${IMP_STASH_PREFIX}refresh`,
  userId: `${IMP_STASH_PREFIX}user_id`,
  isAdmin: `${IMP_STASH_PREFIX}is_admin`,
  adminUserId: `${IMP_STASH_PREFIX}admin_user_id`,
  userType: `${IMP_STASH_PREFIX}user_type`,
  // Tri-state. "true"/"false" mirror the cookie; "" means the admin's
  // session had no agreement cookie (the role doesn't require one). Restore
  // path translates "" back to a Cookies.remove so the admin's atom doesn't
  // re-init from the impersonated reviewer's stale "false"/"true" cookie.
  agreementAccepted: `${IMP_STASH_PREFIX}agreement_accepted`,
  // "" means the admin's session had no agreement type cookie.
  agreementType: `${IMP_STASH_PREFIX}agreement_type`,
} as const;

const isLocalhost =
  window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const getSecureCookieOptions = (expiryDays: number) => ({
  expires: expiryDays,
  secure: !isLocalhost,
  sameSite: "strict" as const,
  path: "/",
});

/**
 * Coarse session verdict derived from the stored cookies. Shared by the
 * AuthProvider 60s interval check and PrivateRoute so the "is this session
 * still good?" decision lives in one place rather than two drifting ladders.
 *
 *  - "active":      access token present and unexpired — proceed.
 *  - "refreshable": access token expired/absent but a refresh token survives —
 *                   attempt a silent refresh.
 *  - "expired":     a session existed but cannot continue (token lapsed with no
 *                   refresh path) — show the Session Expired modal.
 *  - "absent":      no session here — redirect to /login silently, no modal.
 */
export type SessionState = "active" | "refreshable" | "expired" | "absent";

/** Positive, greppable signal that a bail path declared the session over.
 * A silently-shown Session Expired modal is indistinguishable from a bug that
 * never fired, so every gate that pops it logs through here with the
 * originating surface and the verdict that triggered it. */
export function logSessionExpired(source: string, state: SessionState): void {
  console.warn(`[auth] session expired (source=${source}, state=${state})`);
}

interface BackendLoginSuccessResponse {
  access_token: string;
  // Null for impersonation tokens — backend refuses to mint a refresh token
  // for an impersonation (refresh would have to re-derive impersonated_by,
  // which it deliberately doesn't do). Forward the null through so the
  // refresh-cookie is left absent rather than written as the string "null".
  refresh_token: string | null;
  token_type?: string;
  is_admin: boolean;
  agreement_accepted?: boolean | null;
  user_id?: string | null;
  admin_user_id?: string | null;
  user_type?: string | null;
  allowed_cohorts?: string[] | null;
  agreement_type?: string | null;
}

export type UserType = "admin" | "client" | "internal" | "annotator" | "demo_viewer" | "reviewer";

export interface AuthServiceLoginReturnType {
  token: string;
  refreshToken: string | null;
  isAdmin: boolean;
  userId: string;
  agreementAccepted: boolean;
  adminUserId?: string;
  userType?: UserType;
  allowedCohorts?: string[];
  agreementType?: string;
}

/** Shared post-login: persist cookies and build return type */
function handleLoginResponse(
  data: BackendLoginSuccessResponse,
  fallbackUserId: string,
  setCookies: typeof authService.setAuthCookies,
): AuthServiceLoginReturnType {
  const effectiveUserId = data.user_id || fallbackUserId;

  setCookies(
    data.access_token,
    data.refresh_token,
    effectiveUserId,
    data.is_admin,
    data.admin_user_id,
  );

  if (data.user_type) {
    Cookies.set(USER_TYPE_COOKIE_NAME, data.user_type, getSecureCookieOptions(COOKIE_EXPIRY_DAYS));
  }

  // Persist agreement state only when the backend reported one — roles that
  // don't need an agreement (admin/client) return null and should not get a
  // false-value cookie that PrivateRoute would mistake for "not yet signed".
  if (data.agreement_type) {
    Cookies.set(
      AGREEMENT_TYPE_COOKIE_NAME,
      data.agreement_type,
      getSecureCookieOptions(COOKIE_EXPIRY_DAYS),
    );
  } else {
    Cookies.remove(AGREEMENT_TYPE_COOKIE_NAME, { path: "/" });
  }

  if (data.agreement_accepted !== null && data.agreement_accepted !== undefined) {
    Cookies.set(
      AGREEMENT_ACCEPTED_COOKIE_NAME,
      String(data.agreement_accepted),
      getSecureCookieOptions(COOKIE_EXPIRY_DAYS),
    );
  }

  // Any successful new-identity establishment (login / cognitoCallback /
  // setPassword / impersonate — all funnel through here) wipes the tab's
  // query cache, or the prior identity's /api/cohorts, userPrefs, etc. rows
  // survive until staleTime and the new user lands on the wrong worklist
  // (issue #441, same trap as logout — #376). Runs AFTER setCookies so
  // refetches triggered by active observers read the new token. Cost: a
  // same-user re-login also wipes and refetches — rare and acceptable.
  registeredQueryClient?.clear();

  return {
    token: data.access_token,
    refreshToken: data.refresh_token,
    isAdmin: data.is_admin,
    userId: effectiveUserId,
    agreementAccepted: data.is_admin || data.agreement_accepted === true,
    adminUserId: data.admin_user_id || (data.is_admin ? fallbackUserId : undefined),
    userType: (data.user_type as UserType) || undefined,
    allowedCohorts: data.allowed_cohorts || undefined,
    agreementType: data.agreement_type || undefined,
  };
}

export const authService = {
  async login(userIdInput: string, passwordInput: string): Promise<AuthServiceLoginReturnType> {
    const response = await axios.post<BackendLoginSuccessResponse>(
      `${getApiBaseUrl()}/api/auth/login`,
      { user_id: userIdInput, password: passwordInput },
      { headers: { "Content-Type": "application/json" } },
    );
    return handleLoginResponse(response.data, userIdInput, this.setAuthCookies);
  },

  async cognitoCallback(code: string): Promise<AuthServiceLoginReturnType> {
    const redirectUri = getCognitoRedirectUri();
    const response = await axios.post<BackendLoginSuccessResponse>(
      `${getApiBaseUrl()}/api/auth/cognito/callback`,
      null,
      {
        params: { code, redirect_uri: redirectUri },
        headers: { "Content-Type": "application/json" },
      },
    );
    const fallbackUserId = response.data.user_id || response.data.admin_user_id || "unknown";
    return handleLoginResponse(response.data, fallbackUserId, this.setAuthCookies);
  },

  setAuthCookies(
    accessToken: string,
    refreshToken: string | null,
    userId: string,
    isAdmin: boolean,
    adminUserId?: string | null,
  ): void {
    Cookies.set(TOKEN_COOKIE_NAME, accessToken, getSecureCookieOptions(ACCESS_TOKEN_EXPIRY_DAYS));
    // Impersonation tokens have no refresh — clear any stale cookie and skip
    // the write so we don't store the literal "null" string in the jar.
    if (refreshToken) {
      Cookies.set(
        REFRESH_TOKEN_COOKIE_NAME,
        refreshToken,
        getSecureCookieOptions(COOKIE_EXPIRY_DAYS),
      );
    } else {
      Cookies.remove(REFRESH_TOKEN_COOKIE_NAME, { path: "/" });
    }
    Cookies.set(USER_ID_COOKIE_NAME, userId, getSecureCookieOptions(COOKIE_EXPIRY_DAYS));
    Cookies.set(IS_ADMIN_COOKIE_NAME, String(isAdmin), getSecureCookieOptions(COOKIE_EXPIRY_DAYS));

    if (isAdmin && adminUserId) {
      Cookies.set(
        ADMIN_USER_ID_COOKIE_NAME,
        adminUserId,
        getSecureCookieOptions(COOKIE_EXPIRY_DAYS),
      );
    }
  },

  async refreshToken(): Promise<{
    token: string;
    refreshToken: string;
  } | null> {
    const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE_NAME);
    const userId = Cookies.get(USER_ID_COOKIE_NAME);

    if (!refreshToken || !userId) {
      return null;
    }

    try {
      const response = await axios.post<BackendLoginSuccessResponse>(
        `${getApiBaseUrl()}/api/auth/refresh`,
        { refresh_token: refreshToken, user_id: userId },
        { headers: { "Content-Type": "application/json" } },
      );

      const newAccessToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;
      // Backend 401s on impersonation tokens before reaching this path, so a
      // null refresh_token here means the response shape drifted — treat as
      // session failure rather than silently caching a missing refresh.
      if (!newRefreshToken) {
        this.logout();
        return null;
      }

      Cookies.set(
        TOKEN_COOKIE_NAME,
        newAccessToken,
        getSecureCookieOptions(ACCESS_TOKEN_EXPIRY_DAYS),
      );
      Cookies.set(
        REFRESH_TOKEN_COOKIE_NAME,
        newRefreshToken,
        getSecureCookieOptions(COOKIE_EXPIRY_DAYS),
      );

      return { token: newAccessToken, refreshToken: newRefreshToken };
    } catch (err) {
      // #723: only END the session when the refresh is genuinely REJECTED — the
      // refresh token is invalid/expired (401/403). A transient failure (5xx,
      // timeout, network blip) must NOT log the user out: return null so the
      // caller retries on the next interval; the session is still valid.
      // Previously any error logged out, bouncing annotators to /login mid-work
      // (~15 min) and losing unsaved annotations.
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 401 || status === 403) {
        this.logout();
        return null;
      }
      logger.warn("[auth] token refresh failed transiently; will retry next interval:", err);
      return null;
    }
  },

  logout: async () => {
    // Captured before the cookie clear below removes it — the revoke request
    // authenticates with this bearer, not cookies.
    const token = Cookies.get(TOKEN_COOKIE_NAME);
    try {
      Cookies.remove(TOKEN_COOKIE_NAME, { path: "/" });
      Cookies.remove(REFRESH_TOKEN_COOKIE_NAME, { path: "/" });
      Cookies.remove(USER_ID_COOKIE_NAME, { path: "/" });
      Cookies.remove(IS_ADMIN_COOKIE_NAME, { path: "/" });
      Cookies.remove(ADMIN_USER_ID_COOKIE_NAME, { path: "/" });
      Cookies.remove(USER_TYPE_COOKIE_NAME, { path: "/" });
      Cookies.remove(AGREEMENT_ACCEPTED_COOKIE_NAME, { path: "/" });
      Cookies.remove(AGREEMENT_TYPE_COOKIE_NAME, { path: "/" });
      registeredQueryClient?.clear();
    } catch {
      // Logout cleanup failed - continue anyway
    }
    // Server-side revocation (#442): without it the access token stays valid
    // until exp after "logout". Fire-and-forget — local logout never blocks
    // on the network — and skipped for an absent/expired token (the
    // session-expired and refresh-rejected paths land here with a dead token;
    // nothing left to revoke).
    if (token && !isTokenExpired(token)) {
      axios
        .post(`${getApiBaseUrl()}/api/auth/logout`, null, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch((err) => {
          logger.warn("[auth] server-side token revocation failed; token expires naturally:", err);
        });
    }
  },

  isLoggedIn(): boolean {
    // Refresh is a freshness concern, not a login one — impersonation tokens
    // are minted without a refresh by design (see refresh_token contract).
    // Requiring it here would report impersonated sessions as logged-out.
    const accessToken = Cookies.get(TOKEN_COOKIE_NAME);
    const userId = Cookies.get(USER_ID_COOKIE_NAME);
    return Boolean(accessToken && userId);
  },

  /** Classify the stored session for the auth gates. Impersonation tokens
   * carry no refresh by design (the backend refuses to mint one — see the
   * refresh_token contract), so a missing refresh must NOT be read as expiry
   * while the impersonation access token is still live. Only an actually
   * lapsed impersonation token is terminal, since there's no refresh to
   * recover it — that's the 60-min cap, not the instant Assume succeeds. */
  evaluateSession(): SessionState {
    const token = Cookies.get(TOKEN_COOKIE_NAME);
    const refreshToken = Cookies.get(REFRESH_TOKEN_COOKIE_NAME);
    const userId = Cookies.get(USER_ID_COOKIE_NAME);

    // Live access token — nothing to do (covers a fresh impersonation token).
    if (token && !isTokenExpired(token)) return "active";

    // Token expired but still in the jar: impersonation has no refresh path,
    // so it's over rather than refreshable.
    if (token && this.isImpersonating()) return "expired";

    // Normal session: the access cookie lapses a day before the refresh
    // cookie (ACCESS_TOKEN_EXPIRY_DAYS < COOKIE_EXPIRY_DAYS), so an
    // absent/expired access token is recoverable iff a refresh token survives.
    if (refreshToken && userId) return "refreshable";

    // A lingering userId means a session existed here (expired); nothing at
    // all means this browser was never logged in — redirect without a modal.
    return userId ? "expired" : "absent";
  },

  getStoredAuth(): {
    token: string | null;
    refreshToken: string | null;
    userId: string | null;
    isAdmin: boolean;
    adminUserId: string | null;
    userType: UserType | null;
    agreementAccepted: boolean | null;
    agreementType: string | null;
  } {
    const agreementCookie = Cookies.get(AGREEMENT_ACCEPTED_COOKIE_NAME);
    return {
      token: Cookies.get(TOKEN_COOKIE_NAME) || null,
      refreshToken: Cookies.get(REFRESH_TOKEN_COOKIE_NAME) || null,
      userId: Cookies.get(USER_ID_COOKIE_NAME) || null,
      isAdmin: Cookies.get(IS_ADMIN_COOKIE_NAME) === "true",
      adminUserId: Cookies.get(ADMIN_USER_ID_COOKIE_NAME) || null,
      userType: (Cookies.get(USER_TYPE_COOKIE_NAME) as UserType) || null,
      agreementAccepted:
        agreementCookie === "true" ? true : agreementCookie === "false" ? false : null,
      agreementType: Cookies.get(AGREEMENT_TYPE_COOKIE_NAME) || null,
    };
  },

  async acceptAgreement(agreementVersion?: string): Promise<void> {
    const { token, userId } = this.getStoredAuth();
    if (!token || !userId) throw new Error("User not authenticated");

    await axios.post(
      `${getApiBaseUrl()}/api/auth/accept_agreement`,
      agreementVersion ? { agreement_version: agreementVersion } : {},
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );

    // Persist the new state so PrivateRoute stops bouncing this session and so
    // subsequent reloads remember the user already signed.
    Cookies.set(AGREEMENT_ACCEPTED_COOKIE_NAME, "true", getSecureCookieOptions(COOKIE_EXPIRY_DAYS));
  },

  async validateInvite(token: string): Promise<{ valid: boolean; user_type: string }> {
    const response = await axios.get(`${getApiBaseUrl()}/api/auth/validate_invite/${token}`);
    return response.data;
  },

  async requestPasswordReset(email: string): Promise<void> {
    await axios.post(
      `${getApiBaseUrl()}/api/auth/request_password_reset`,
      { email },
      { headers: { "Content-Type": "application/json" } },
    );
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    await axios.post(
      `${getApiBaseUrl()}/api/auth/reset_password`,
      { token, new_password: newPassword },
      { headers: { "Content-Type": "application/json" } },
    );
  },

  /** Set the recipient's password from the invite token and auto-login.
   *
   * Backend issues an access+refresh pair on the same call so the recipient
   * skips a redundant /login bounce — the cookies are persisted here and the
   * caller can navigate straight to postAuthDestination. AgreementGate will
   * still gate access until the user accepts their role's agreement.
   */
  async setPassword(
    email: string,
    inviteToken: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<AuthServiceLoginReturnType> {
    const response = await axios.post<BackendLoginSuccessResponse>(
      `${getApiBaseUrl()}/api/auth/set_password`,
      {
        email,
        invite_token: inviteToken,
        new_password: newPassword,
        confirm_password: confirmPassword,
      },
      { headers: { "Content-Type": "application/json" } },
    );
    return handleLoginResponse(response.data, email, this.setAuthCookies);
  },

  async getAdminUsers(): Promise<{ users: AdminUser[] }> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    const response = await axios.get(`${getApiBaseUrl()}/api/admin/users`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async inviteExternalUser(
    email: string,
    role: string,
    cohorts: string[],
    projects: string[] = [],
    ttlDays?: number,
    companyId?: string,
  ): Promise<{ invite_token: string }> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    const body: Record<string, unknown> = { email, role, cohorts, projects };
    if (ttlDays !== undefined) body.ttl_days = ttlDays;
    if (companyId) body.company_id = companyId;
    const response = await axios.post(`${getApiBaseUrl()}/api/admin/invite_external_user`, body, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  },

  async updateUserCohorts(userId: string, cohorts: string[]): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.patch(
      `${getApiBaseUrl()}/api/admin/users/${encodeURIComponent(userId)}/cohorts`,
      { allowed_cohorts: cohorts },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  async updateUserProjects(userId: string, projects: string[]): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.patch(
      `${getApiBaseUrl()}/api/admin/users/${encodeURIComponent(userId)}/projects`,
      { allowed_projects: projects },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  /** Disable or re-enable an external user. A disabled account is refused on its
   *  next request and cannot sign in; internal accounts cannot be disabled. */
  async setUserDisabled(userId: string, disabled: boolean): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.patch(
      `${getApiBaseUrl()}/api/admin/users/${encodeURIComponent(userId)}/disabled`,
      { disabled },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  async deleteUser(userId: string): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.delete(`${getApiBaseUrl()}/api/admin/users/${encodeURIComponent(userId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  async resendInvite(userId: string): Promise<{ invite_token: string }> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    const response = await axios.post(
      `${getApiBaseUrl()}/api/admin/resend_invite/${encodeURIComponent(userId)}`,
      {},
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  // ===========================================================================
  // Company management
  // ===========================================================================

  async getCompanies(): Promise<Company[]> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    const response = await axios.get(`${getApiBaseUrl()}/api/admin/companies`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  },

  async createCompany(displayName: string, customerCode: string): Promise<Company> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    const response = await axios.post(
      `${getApiBaseUrl()}/api/admin/companies`,
      { display_name: displayName, customer_code: customerCode },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  },

  async getCompany(companyId: string): Promise<CompanyDetail> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    const response = await axios.get(
      `${getApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    return response.data;
  },

  async renameCompany(companyId: string, displayName: string): Promise<Company> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    const response = await axios.patch(
      `${getApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyId)}`,
      { display_name: displayName },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
    return response.data;
  },

  async deleteCompany(companyId: string): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.delete(`${getApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyId)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
  },

  async assignCompanyCohort(companyId: string, cohortId: string): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.post(
      `${getApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyId)}/cohorts`,
      { cohort_id: cohortId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  async unassignCompanyCohort(companyId: string, cohortId: string): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.delete(
      `${getApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyId)}/cohorts/${encodeURIComponent(cohortId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  },

  async addCompanyMember(companyId: string, userId: string): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.post(
      `${getApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyId)}/members`,
      { user_id: userId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  async removeCompanyMember(companyId: string, userId: string): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.delete(
      `${getApiBaseUrl()}/api/admin/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(userId)}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
  },

  /** Set (companyId) or clear (null) a user's company membership. */
  async setUserCompany(userId: string, companyId: string | null): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.patch(
      `${getApiBaseUrl()}/api/admin/users/${encodeURIComponent(userId)}/company`,
      { company_id: companyId },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  /** Set a user's granted capabilities (the full desired set; diffed server-side).
   *  Takes effect on the user's next token refresh. */
  async setUserCapabilities(userId: string, capabilities: string[]): Promise<void> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    await axios.patch(
      `${getApiBaseUrl()}/api/admin/users/${encodeURIComponent(userId)}/capabilities`,
      { capabilities },
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      },
    );
  },

  // ===========================================================================
  // Admin impersonation
  // ===========================================================================

  /** Stash the current admin tokens in sessionStorage so stopImpersonating can
   * restore them. Caller must already hold an admin session.
   */
  _stashAdminTokens(): void {
    const stored = this.getStoredAuth();
    if (!stored.token || !stored.userId) {
      throw new Error("No admin session to stash");
    }
    sessionStorage.setItem(IMP_KEYS.token, stored.token);
    sessionStorage.setItem(IMP_KEYS.refreshToken, stored.refreshToken || "");
    sessionStorage.setItem(IMP_KEYS.userId, stored.userId);
    sessionStorage.setItem(IMP_KEYS.isAdmin, String(stored.isAdmin));
    sessionStorage.setItem(IMP_KEYS.adminUserId, stored.adminUserId || stored.userId);
    sessionStorage.setItem(IMP_KEYS.userType, stored.userType || "");
    // Read the raw cookie so absent (admin/client roles) round-trips as "".
    sessionStorage.setItem(
      IMP_KEYS.agreementAccepted,
      Cookies.get(AGREEMENT_ACCEPTED_COOKIE_NAME) ?? "",
    );
    sessionStorage.setItem(IMP_KEYS.agreementType, Cookies.get(AGREEMENT_TYPE_COOKIE_NAME) ?? "");
  },

  /** True iff the current access token was minted as an impersonation. Derived
   * from the JWT's `impersonated_by` claim so every tab sharing the cookie
   * sees the same state — sessionStorage is per-tab and would let tab B
   * silently render an impersonated session with no banner.
   */
  isImpersonating(): boolean {
    const token = Cookies.get(TOKEN_COOKIE_NAME);
    if (!token) return false;
    const claims = decodeToken(token) as { impersonated_by?: unknown } | null;
    return typeof claims?.impersonated_by === "string" && claims.impersonated_by.length > 0;
  },

  async impersonate(
    targetUserId: string,
    ttlMinutes = 60,
    reason?: string,
  ): Promise<AuthServiceLoginReturnType> {
    const { token } = this.getStoredAuth();
    if (!token) throw new Error("Not authenticated");
    // Stash BEFORE the request so a partial failure doesn't strand us. Note:
    // if the page is navigated away while this request is in flight, the
    // stash leaks until the tab closes (sessionStorage scope). Acceptable —
    // worst case the user sees the impersonation banner pointing at no
    // identity, which Stop-impersonating clears.
    this._stashAdminTokens();
    try {
      const response = await axios.post<BackendLoginSuccessResponse>(
        `${getApiBaseUrl()}/api/admin/impersonate`,
        { target_user_id: targetUserId, ttl_minutes: ttlMinutes, reason },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // handleLoginResponse clears the query cache, so admin's cached rows
      // can't appear under the impersonated identity (see #376/#441).
      return handleLoginResponse(response.data, targetUserId, this.setAuthCookies);
    } catch (err) {
      // Failed mint — drop the stash so we don't think we're impersonating.
      for (const k of Object.values(IMP_KEYS)) sessionStorage.removeItem(k);
      throw err;
    }
  },

  /** Restore the admin session previously stashed by `impersonate`. Async
   * because the no-stash branch awaits `logout()`'s cookie clear — caller
   * navigates immediately after, and unawaited the clear can race the next
   * cookie read. */
  async stopImpersonating(): Promise<void> {
    const token = sessionStorage.getItem(IMP_KEYS.token);
    const refreshToken = sessionStorage.getItem(IMP_KEYS.refreshToken);
    const userId = sessionStorage.getItem(IMP_KEYS.userId);
    const isAdmin = sessionStorage.getItem(IMP_KEYS.isAdmin) === "true";
    const adminUserId = sessionStorage.getItem(IMP_KEYS.adminUserId);
    const userType = sessionStorage.getItem(IMP_KEYS.userType);
    const agreementAccepted = sessionStorage.getItem(IMP_KEYS.agreementAccepted);
    const agreementType = sessionStorage.getItem(IMP_KEYS.agreementType);
    if (!token || !userId) {
      // No stash — caller should not have been in impersonation state. Hard
      // clear and bounce to login; safer than restoring partial state.
      await this.logout();
      return;
    }
    this.setAuthCookies(token, refreshToken || null, userId, isAdmin, adminUserId);
    // Restore the admin's pre-impersonation user_type. Stashed "" means the
    // admin had no user_type cookie (e.g. the local-dev admin session) — remove
    // the impersonated user's leftover value rather than leaving "reviewer" in
    // the jar. PrivateRoute reads a stale "reviewer" as a cohort-bound reviewer
    // and, since the restored admin token carries no allowed_cohorts, bounces
    // the admin all the way to /login. Same tri-state handling as the agreement
    // cookie below.
    if (userType) {
      Cookies.set(USER_TYPE_COOKIE_NAME, userType, getSecureCookieOptions(COOKIE_EXPIRY_DAYS));
    } else {
      Cookies.remove(USER_TYPE_COOKIE_NAME, { path: "/" });
    }
    // Restore the admin's pre-impersonation agreement cookie. The
    // impersonated user's value is left in the jar by setAuthCookies; a
    // refresh would re-init the atom from it without this restore. Stashed
    // "" means the admin had no cookie — remove rather than write a string.
    if (agreementAccepted) {
      Cookies.set(
        AGREEMENT_ACCEPTED_COOKIE_NAME,
        agreementAccepted,
        getSecureCookieOptions(COOKIE_EXPIRY_DAYS),
      );
    } else {
      Cookies.remove(AGREEMENT_ACCEPTED_COOKIE_NAME, { path: "/" });
    }
    if (agreementType) {
      Cookies.set(
        AGREEMENT_TYPE_COOKIE_NAME,
        agreementType,
        getSecureCookieOptions(COOKIE_EXPIRY_DAYS),
      );
    } else {
      Cookies.remove(AGREEMENT_TYPE_COOKIE_NAME, { path: "/" });
    }
    for (const k of Object.values(IMP_KEYS)) sessionStorage.removeItem(k);
    // Drop the impersonated user's cached query rows so admin's restored
    // session doesn't read them (same trap as logout — see #376).
    registeredQueryClient?.clear();
  },
};

export interface Grant {
  grant_type: "cohort" | "project";
  grant_id: string;
  granted_by: string | null;
  granted_at: string | null;
  expires: string | null;
}

export interface AdminUser {
  user_id: string;
  email?: string;
  user_type: string;
  disabled?: boolean;
  allowed_cohorts?: string[];
  allowed_projects?: string[];
  has_pending_invite: boolean;
  has_accepted_agreement: boolean;
  agreement_accepted_at?: string;
  invited_by?: string;
  created_at?: string;
  invite_token?: string;
  grants?: Grant[];
  company_id?: string;
  /** Cohorts inherited from the user's company + the user's per-user blocks —
   *  the editor renders the effective set (own ∪ inherited − blocked). */
  inherited_cohorts?: string[];
  blocked_cohorts?: string[];
  /** Granted action capabilities (e.g. "upload"). Internal/admin pass gates
   *  implicitly and carry an empty list here regardless. */
  capabilities?: string[];
}

export interface Company {
  id: string;
  display_name: string;
  customer_code?: string;
  member_count: number;
  cohort_count: number;
}

export interface CompanyCohort {
  cohort_id: string;
  granted_by: string | null;
  granted_at: string | null;
}

export interface CompanyMember {
  user_id: string;
  email?: string;
  user_type: string;
}

export interface CompanyDetail {
  id: string;
  display_name: string;
  customer_code?: string;
  cohorts: CompanyCohort[];
  members: CompanyMember[];
  /** Cohort ids already claimed by other companies — excluded from this
   *  company's assignable picker (exclusive-claim tenancy). */
  assigned_elsewhere?: string[];
}

/** Server-side cap: reject durations above 2 hours */
const MAX_CASE_DURATION_SECONDS = 7200;

/** Record time spent on a case (fire-and-forget with keepalive for page unload). */
export async function recordCaseTime(
  projectId: string,
  studyInstanceUid: string,
  elapsedSeconds: number,
): Promise<void> {
  const stored = authService.getStoredAuth();
  if (!stored.token) return;
  const url = `${getApiBaseUrl()}/api/labeling/projects/${encodeURIComponent(projectId)}/case-time`;
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${stored.token}`,
      },
      body: JSON.stringify({
        study_instance_uid: studyInstanceUid,
        duration_seconds: Math.min(elapsedSeconds, MAX_CASE_DURATION_SECONDS),
      }),
      keepalive: true,
    });
    if (!response.ok) {
      console.warn(`[CaseTime] Server rejected case time (${response.status})`);
    }
  } catch (err) {
    console.warn("[CaseTime] Failed to record case time:", err);
  }
}

/**
 * Send segmentation-study case-time events (ENG-202). Never throws: the result
 * tells the queue what to do with the batch (API contract §1).
 */
export async function recordCaseEvents(
  projectId: string,
  events: CaseEventIn[],
  { keepalive = false }: { keepalive?: boolean } = {},
): Promise<RecordCaseEventsResult> {
  const stored = authService.getStoredAuth();
  // Signed out (or token not readable yet): keep the events for later.
  if (!stored.token) return { kind: "retry" };
  const url = `${getApiBaseUrl()}/api/labeling/projects/${encodeURIComponent(projectId)}/case-events`;
  let response: Response;
  try {
    response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${stored.token}`,
      },
      body: JSON.stringify({ events }),
      keepalive,
    });
  } catch (err) {
    logger.warn("[CaseEvents] network error:", err);
    return { kind: "retry" };
  }
  if (response.ok) {
    try {
      const body = await response.json();
      return {
        kind: "ok",
        accepted: Array.isArray(body?.accepted) ? body.accepted : [],
        duplicate: Array.isArray(body?.duplicate) ? body.duplicate : [],
        rejected: Array.isArray(body?.rejected) ? body.rejected : [],
      };
    } catch {
      // Unreadable 200: resending is safe (event_id dedupes).
      return { kind: "retry" };
    }
  }
  const status = response.status;
  if (status === 404) return { kind: "not_study" };
  if (status >= 500 || status === 401 || status === 408 || status === 429) return { kind: "retry" };
  logger.warn(`[CaseEvents] server rejected the batch (${status})`);
  return { kind: "drop" };
}
